
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import CSSModule from 'react-css-modules';
// import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';

import { AppWrapper, CustomerCouponCard, Spinner } from 'components';
import { actions as categoryActions } from 'coupon-common/src/modules/customerCoupons/categories';
import { actions as categorizedActions } from 'coupon-common/src/modules/customerCoupons/categorized';
import { actions as favouriteActions } from 'coupon-common/src/modules/customerCoupons/favourite';
import { dispatchAsAsync } from 'coupon-common/src/utils';

import styles from './CustomerCouponListAll.module.scss';

const FEED_ID = 'search';

export const CustomerCouponListAll = () => {
  const dispatch = useDispatch();
//   const navigate = useNavigate();

  const allState = useSelector(state => state.customerCoupons.categorized);
  const { fetching, fetched, items, result, error } = allState;
  const { results, nextPage } = result || {};

  const followState = useSelector(state => state.businesses.follow);
  const allFavourite = useSelector(state => state.customerCoupons.favourite);
  const favouriteList = allFavourite && allFavourite.data;

  const categoryState = useSelector(state => (state.customerCoupons.categories) || {});
  const dynamicCategories = (categoryState.data && categoryState.data.results) || categoryState.data || [];

  const activeCityId = useSelector(state => state.cities.activeCityId);

  // -------------------- LOCAL STATE --------------------
  const [searchKey, setSearchKey] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [dynamicSubcategories, setDynamicSubcategories] = useState([]);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSubCategoryOpen, setIsSubCategoryOpen] = useState(false);
  const [isFavoritesActive, setIsFavoritesActive] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const manuallySelectedSubcategories = useRef(false);
  const nextPageRef = useRef(null);
  const lastQueryKeyRef = useRef(null);
  const resultsLengthRef = useRef(0);
  const isLoadingMoreRef = useRef(false);
  const categoryDropdownRef = useRef(null);
  const subCategoryDropdownRef = useRef(null);
  const sentinelRef = useRef(null);

  // -------------------- NAVIGATION --------------------
  const goToDetails = couponId => {
    // navigate(`/coupons/${couponId}`);
    // console.log(`[CustomerCouponListAll] goToDetails called with couponId: ${couponId}`);
  };

  const goToBusinessDetails = businessId => {
    // console.log(`[CustomerCouponListAll] goToBusinessDetails called with businessId: ${businessId}`);
    // navigate(`/businesses/${businessId}`);
  };

  // -------------------- LOAD CATEGORIES ON MOUNT --------------------
  useEffect(() => {
    dispatch(categoryActions.getCategoryList());
  }, [dispatch]);

  // -------------------- LIST BUILDING --------------------
  const list = useMemo(() => {
    if (!results || !items) return [];
    return results.map(res => items[res]).filter(Boolean);
  }, [results, items]);

  // -------------------- FAVORITES FILTER --------------------
  const favoriteCoupons = useMemo(() => {
    if (!favouriteList || !favouriteList.results) return [];

    let favList = Array.isArray(favouriteList.results) ? favouriteList.results : [];

    if (isFavoritesActive && selectedCategories.length > 0) {
      favList = favList.filter(
        coupon => coupon.category && selectedCategories.includes(coupon.category.id),
      );
    }

    return favList;
  }, [favouriteList, selectedCategories, isFavoritesActive]);

  // -------------------- SEARCH + CATEGORY FILTER --------------------
  const filteredList = useMemo(() => {
    let templist = [...list];

    if (searchKey.trim()) {
      const lowerSearchKey = searchKey.toLowerCase();
      templist = templist.filter(item => {
        const titleMatch = item.title && item.title.toLowerCase().includes(lowerSearchKey);
        const descriptionMatch =
          item.description && item.description.toLowerCase().includes(lowerSearchKey);
        return titleMatch || descriptionMatch;
      });
    }

    if (selectedCategories.length === 0) return templist;

    if (selectedSubCategories.length > 0) {
      templist = templist.filter(coupon => {
        const subcategoryIds = (coupon.subcategories && coupon.subcategories.map(s => s.id)) || [];
        return subcategoryIds.some(id => selectedSubCategories.includes(id));
      });
    }

    return templist.filter(coupon => {
      if (!coupon.category) return false;
      return selectedCategories.includes(coupon.category.id);
    });
  }, [list, searchKey, selectedCategories, selectedSubCategories]);

  // -------------------- QUERY KEY (drives fetch + pagination reset) --------------------
  const queryKey = useMemo(() => {
    return JSON.stringify({
      cat: selectedCategories[0] || null,
      sub: selectedSubCategories[0] || null,
      city: activeCityId || null,
    });
  }, [selectedCategories, selectedSubCategories, activeCityId]);

  // -------------------- FETCH FUNCTION --------------------
  const getAllListRef = useRef();

  useEffect(() => {
    getAllListRef.current = (params = {}) => {
      const finalParams = {
        category: selectedCategories[0] || null,
        subcategory: selectedSubCategories[0] || null,
        published_only: true,
        city: activeCityId || null,
        page: params.page || 1,
      };
      dispatch(categorizedActions.getAllList(finalParams));
    };
  }, [selectedCategories, selectedSubCategories, activeCityId, dispatch]);

  // -------------------- TRACK NEXT PAGE / LOADING STATE --------------------
  useEffect(() => {
    if (typeof nextPage !== 'undefined') {
      nextPageRef.current = { key: queryKey, value: nextPage };
    }

    const currentLen = (results && results.length) || 0;
    if (loadingNextPage && currentLen > resultsLengthRef.current) {
      setLoadingNextPage(false);
      isLoadingMoreRef.current = false;
    }
    resultsLengthRef.current = currentLen;

    if (isTransitioning && !fetching) {
      setIsTransitioning(false);
    }
  }, [nextPage, results, loadingNextPage, queryKey, isTransitioning, fetching]);

  // -------------------- QUERY KEY CHANGE -> RESET + REFETCH --------------------
  useEffect(() => {
    if (lastQueryKeyRef.current === queryKey) return;

    lastQueryKeyRef.current = queryKey;
    nextPageRef.current = null;
    resultsLengthRef.current = 0;
    setLoadingNextPage(false);
    isLoadingMoreRef.current = false;
    setIsTransitioning(true);
    dispatch(categorizedActions.resetCouponList());
    getAllListRef.current({ page: 1 });
  }, [queryKey, dispatch]);

  // -------------------- SUBCATEGORY MAPPING --------------------
  useEffect(() => {
    const selectedCategoryId = selectedCategories[0];
    const matchedCategory = dynamicCategories.find(cat => cat.id === selectedCategoryId);

    if (!matchedCategory || !matchedCategory.subcategories || matchedCategory.subcategories.length === 0) {
      if (dynamicSubcategories.length > 0) setDynamicSubcategories([]);
      if (selectedSubCategories.length > 0) setSelectedSubCategories([]);
      return;
    }

    const subcategoryIdsInCoupons = new Set();
    list.forEach(coupon => {
      (coupon.subcategories || []).forEach(sub => {
        subcategoryIdsInCoupons.add(sub.id);
      });
    });

    const validSubcategories = matchedCategory.subcategories.filter(sub =>
      subcategoryIdsInCoupons.has(sub.id),
    );

    const isEqual = JSON.stringify(dynamicSubcategories) === JSON.stringify(validSubcategories);

    if (!isEqual) {
      setDynamicSubcategories(validSubcategories);
      if (!manuallySelectedSubcategories.current) {
        setSelectedSubCategories([]);
      }
    }

    manuallySelectedSubcategories.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories, dynamicCategories, list]);

  // -------------------- FAVORITE HANDLING --------------------
  const toggleFavouriteHandler = (id, isFavorite) => {
    const action = isFavorite
      ? favouriteActions.removeFavourite(id)
      : favouriteActions.markAsFavourite(id);
    dispatchAsAsync(action, dispatch);
  };

  const checkFavourite = useCallback(
    coupon => {
      return (
        (favouriteList &&
          favouriteList.results &&
          favouriteList.results.some(favCoupon => favCoupon.id === coupon.id)) ||
        false
      );
    },
    [favouriteList],
  );

  // -------------------- PAGINATION --------------------
  const handleLoadMore = useCallback(() => {
    if (isLoadingMoreRef.current) return;

    const isValidNext = nextPageRef.current && nextPageRef.current.key === queryKey;
    const next = isValidNext ? nextPageRef.current.value : null;

    if (!next) return;

    isLoadingMoreRef.current = true;
    setLoadingNextPage(true);
    getAllListRef.current({ page: next });
  }, [queryKey]);

  // -------------------- INFINITE SCROLL (IntersectionObserver) --------------------
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          handleLoadMore();
        }
      },
      { rootMargin: '400px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  // -------------------- CLICK OUTSIDE TO CLOSE DROPDOWNS --------------------
  useEffect(() => {
    const handleClickOutside = e => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) {
        setIsCategoryOpen(false);
      }
      if (subCategoryDropdownRef.current && !subCategoryDropdownRef.current.contains(e.target)) {
        setIsSubCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // -------------------- DISPLAY DATA --------------------
  const displayData = isFavoritesActive ? favoriteCoupons : filteredList || [];

  // -------------------- RENDER GUARDS --------------------
  const showError = error && !fetched;
  const showLoading = !filteredList || (fetching && !fetched) || isTransitioning;

  return (
    <AppWrapper showHeadingRow headingText="Coupons">
      <div styleName="search-bar-wrap">
        <input
          type="text"
          styleName="search-input"
          placeholder="Search coupons..."
          value={searchKey}
          onChange={e => setSearchKey(e.target.value)}
        />
      </div>

      <div styleName="filter-strip">
        <div styleName="chip-wrapper" ref={categoryDropdownRef}>
          <button
            type="button"
            styleName="chip"
            onClick={() => setIsCategoryOpen(!isCategoryOpen)}
          >
            <span styleName="chip-text">
              {selectedCategories.length > 0
                ? (dynamicCategories.find(c => c.id === selectedCategories[0]) || {}).name
                : 'Categories'}
            </span>
            {isCategoryOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {isCategoryOpen && (
            <div styleName="dropdown">
              <button
                type="button"
                styleName="dropdown-item"
                onClick={() => {
                  setSelectedCategories([]);
                  setIsCategoryOpen(false);
                }}
              >
                All categories
              </button>
              {dynamicCategories.map(cat => (
                <button
                  type="button"
                  key={cat.id}
                  styleName="dropdown-item"
                  onClick={() => {
                    setSelectedCategories([cat.id]);
                    setIsCategoryOpen(false);
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {dynamicSubcategories.length > 0 && (
          <div styleName="chip-wrapper" ref={subCategoryDropdownRef}>
            <button
              type="button"
              styleName="chip"
              onClick={() => setIsSubCategoryOpen(!isSubCategoryOpen)}
            >
              <span styleName="chip-text">
                {selectedSubCategories.length > 0
                  ? dynamicSubcategories
                      .filter(sub => selectedSubCategories.includes(sub.id))
                      .map(sub => sub.name)
                      .join(', ')
                  : 'Sub-Categories'}
              </span>
              {isSubCategoryOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {isSubCategoryOpen && (
              <div styleName="dropdown">
                {dynamicSubcategories.map(sub => {
                  const isSelected = selectedSubCategories.includes(sub.id);
                  return (
                    <button
                      type="button"
                      key={sub.id}
                      styleName="dropdown-item"
                      onClick={() => {
                        manuallySelectedSubcategories.current = true;
                        setSelectedSubCategories(
                          isSelected
                            ? selectedSubCategories.filter(id => id !== sub.id)
                            : [...selectedSubCategories, sub.id],
                        );
                      }}
                    >
                      {sub.name} {isSelected ? '✓' : ''}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div styleName="chip-wrapper">
          <button
            type="button"
            styleName={isFavoritesActive ? 'chip chip-active' : 'chip'}
            onClick={() => {
              setIsFavoritesActive(!isFavoritesActive);
              if (!isFavoritesActive) {
                dispatch(favouriteActions.getAllFavouriteList());
              }
            }}
          >
            <span styleName="chip-text">Favourites</span>
          </button>
        </div>
      </div>

      {showError && <div styleName="state-message">Something went wrong loading coupons.</div>}

      {showLoading && !showError && (
        <div styleName="state-message">
          <Spinner show size="md" />
        </div>
      )}

      {!showLoading && !showError && (
        <>
          {displayData.length === 0 ? (
            <div styleName="state-message">No coupons found.</div>
          ) : (
            <div styleName="grid">
              {displayData.map((item, index) => (
                <CustomerCouponCard
                  key={item.id}
                  first={index === 0}
                  data={item}
                  onPress={() => goToDetails(item.id)}
                  goToBusinessDetails={() =>
                    goToBusinessDetails(item.business && item.business.id)
                  }
                  toggleFavourite={() => toggleFavouriteHandler(item.id, checkFavourite(item))}
                  isFavourite={checkFavourite(item)}
                />
              ))}
            </div>
          )}
          <div ref={sentinelRef} styleName="scroll-sentinel" />
          {loadingNextPage && (
            <div styleName="state-message">
              <Spinner show size="sm" />
            </div>
          )}
        </>
      )}
    </AppWrapper>
  );
};

export default CSSModule(CustomerCouponListAll, styles, { allowMultiple: true });